import { supabase } from '../clients/supabaseClient';
import type { Store } from '../types';

const STORE_SELECT = `
    id,
    name,
    phone_number,
    address_line1,
    suburb,
    city,
    radius_km,
    created_at,
    default_store,
    trading_hours,
    latitude,
    longitude,
    location_text:location
`;

export type StorePayload = {
    name: string;
    phone_number?: string | null;
    address_line1: string;
    suburb?: string | null;
    city: string;
    radius_km?: number | null;
    default_store: boolean;
    trading_hours?: string | null;
    location?: string | null;
    latitude?: number | null;
    longitude?: number | null;
};

export async function fetchStoresFromDb(): Promise<Store[]> {
    const { data, error } = await supabase
        .from('stores')
        .select(STORE_SELECT)
        .order('default_store', { ascending: false })
        .order('name', { ascending: true });

    if (error) throw error;

    return (data ?? []).map((item) => ({
        ...item,
        location: item.location_text,
    })) as Store[];
}

export async function fetchStoreByIdFromDb(storeId: string): Promise<Store | null> {
    const { data, error } = await supabase
        .from('stores')
        .select(STORE_SELECT)
        .eq('id', storeId)
        .single();

    if (error) throw error;
    return { ...data, location: (data).location_text } as Store;
}

export async function createStoreInDb(payload: StorePayload): Promise<Store> {
    const { data, error } = await supabase
        .from('stores')
        .insert(payload)
        .select(STORE_SELECT)
        .single();

    if (error) throw error;
    return { ...data, location: (data).location_text } as Store;
}

export async function updateStoreInDb(id: string, payload: Partial<StorePayload>): Promise<Store> {
    const { data, error } = await supabase
        .from('stores')
        .update(payload)
        .eq('id', id)
        .select(STORE_SELECT)
        .single();

    if (error) throw error;
    return { ...data, location: (data).location_text } as Store;
}

export async function deleteStoreFromDb(id: string): Promise<void> {
    const { error } = await supabase.from('stores').delete().eq('id', id);
    if (error) throw error;
}

export async function clearDefaultStoreExcept(excludeId: string): Promise<void> {
    const { error } = await supabase
        .from('stores')
        .update({ default_store: false })
        .neq('id', excludeId);

    if (error) throw error;
}

export async function clearAllDefaultStoresFromDb(): Promise<void> {
    const { error } = await supabase
        .from('stores')
        .update({ default_store: false })
        .eq('default_store', true);
    if (error) throw error;
}

export async function getLinkedProductCount(storeId: string): Promise<number> {
    const { count, error } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('store_id', storeId);

    if (error) throw error;
    return count ?? 0;
}